/**
 * 📬 MATCHING-MODAL
 * Zeigt wartende Kunden die zum neu gespeicherten Paket passen
 * Ermöglicht das Versenden von Benachrichtigungen
 */

import { useState, useEffect } from 'react';
import { markAsNotified } from './waitingListHelper';
import { useTranslation } from './hooks/useTranslation';

function MatchingModal({ matches = [], paket, onClose, onNotify, colors }) {
  const { t } = useTranslation();
  const [notifying, setNotifying] = useState(new Set());
  const [notifiedIds, setNotifiedIds] = useState(new Set());
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Auto-Hide Toast nach 3 Sekunden
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const handleNotify = async (match) => {
    if (notifiedIds.has(match.id)) {
      setToast({ show: true, message: `✅ ${t('matching.alreadyNotified')}`, type: 'info' });
      return;
    }

    const confirmText = `${t('matching.confirmNotification')}\n\n${match.name}\n${match.strasse} ${match.hausnummer}\n\n${t('matching.contact')}: ${match.kontakt} (${match.kontaktTyp})\n\n${t('matching.continue')}`;
    
    if (!window.confirm(confirmText)) {
      return;
    }

    setNotifying(prev => new Set(prev).add(match.id));

    try {
      // Markiere als benachrichtigt in Firestore
      await markAsNotified(match.id, paket.id);

      // Rufe Notification-Funktion auf (sendet Email/SMS)
      if (onNotify) {
        await onNotify(match, paket);
      }

      // Markiere als erfolgreich
      setNotifiedIds(prev => new Set(prev).add(match.id));
      setToast({ show: true, message: `✅ ${t('matching.notificationSentTo', { name: match.name })}`, type: 'success' });

    } catch (error) {
      console.error('Fehler beim Benachrichtigen:', error);
      setToast({ show: true, message: `❌ ${t('matching.notificationError')}`, type: 'error' });
    } finally {
      setNotifying(prev => {
        const newSet = new Set(prev);
        newSet.delete(match.id);
        return newSet;
      });
    }
  };

  // Maskiere Telefonnummer (zeige nur letzte 4 Ziffern)
  const maskKontakt = (kontakt, kontaktTyp) => {
    if (kontaktTyp === 'telefon') {
      const digits = kontakt.replace(/\D/g, '');
      if (digits.length >= 4) {
        return '...' + digits.slice(-4);
      }
    } else if (kontaktTyp === 'email') {
      const [user, domain] = kontakt.split('@');
      if (user && domain) {
        return user.slice(0, 2) + '***@' + domain;
      }
    }
    return kontakt;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        backgroundColor: '#FFF9C4', // Gelb (hell)
        borderRadius: '16px',
        padding: '30px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        border: '3px solid #FFD54F' // Gelb (dunkler)
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '25px',
          borderBottom: '2px solid #FFB300',
          paddingBottom: '15px'
        }}>
          <h2 style={{
            margin: '0 0 10px 0',
            color: '#F57C00',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            ⚠️ {t('matching.similarMatchesFound')}
          </h2>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '14px'
          }}>
            {matches.length === 1 
              ? t('matching.customersWaitingSingular', { count: matches.length })
              : t('matching.customersWaitingPlural', { count: matches.length })}
          </p>
        </div>

        {/* Gerade gespeichertes Paket */}
        <div style={{
          backgroundColor: '#E1F5FE', // Blau (hell)
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '25px',
          border: '2px solid #81D4FA'
        }}>
          <h3 style={{
            margin: '0 0 10px 0',
            color: '#0277BD',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            📦 {t('matching.justSaved')}
          </h3>
          <div style={{ fontSize: '15px', color: '#333' }}>
            <strong>{paket.name}</strong><br />
            {paket.streetAndNumber}
          </div>
        </div>

        {/* Wartende Kunden */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{
            margin: '0 0 15px 0',
            color: '#E65100',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            👥 {t('matching.waitingCustomers')}
          </h3>

          {matches.map((match, index) => (
            <div
              key={match.id}
              style={{
                backgroundColor: notifiedIds.has(match.id) ? '#C8E6C9' : '#fff',
                padding: '15px',
                marginBottom: '12px',
                borderRadius: '8px',
                border: `2px solid ${notifiedIds.has(match.id) ? '#4CAF50' : '#ddd'}`,
                position: 'relative'
              }}
            >
              {/* Match-Score Badge */}
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: match.matchScore >= 80 ? '#4CAF50' : match.matchScore >= 60 ? '#FF9800' : '#FFC107',
                color: '#fff',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {match.matchScore}% Match
              </div>

              <div style={{ marginBottom: '10px', paddingRight: '90px' }}>
                <strong style={{ fontSize: '16px', color: '#333' }}>
                  {match.name}
                </strong>
                {match.isAddressBook && (
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 7px',
                    borderRadius: '6px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    fontSize: '11px',
                    fontWeight: 700,
                  }}>
                    📒 Liste / Stammkunde
                  </span>
                )}
                <br />
                {(match.strasse || match.hausnummer) && (
                  <span style={{ color: '#666', fontSize: '14px' }}>
                    {match.strasse} {match.shelf ? `(🗄️ Fach ${match.shelf})` : match.hausnummer}
                  </span>
                )}
              </div>

              {match.note && (
                <div
                  data-testid="matching-modal-note"
                  style={{
                    marginBottom: '10px',
                    padding: '8px 10px',
                    backgroundColor: '#fff3cd',
                    border: '1.5px solid #ffecb5',
                    borderRadius: '6px',
                    color: '#664d03',
                    fontSize: '13px',
                    fontWeight: 700,
                  }}
                >
                  📌 Hinweis für Mitarbeiter: {match.note}
                </div>
              )}

              <div style={{
                fontSize: '13px',
                color: '#666',
                marginBottom: '12px',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px'
              }}>
                {match.kontakt ? (
                  <>
                    <strong>{match.kontaktTyp === 'telefon' ? '📱' : '📧'} {t('matching.contact')}:</strong> {maskKontakt(match.kontakt, match.kontaktTyp)}
                    <br />
                  </>
                ) : (
                  <>
                    <strong>ℹ️ Kontakt:</strong> Keine E-Mail hinterlegt (nur Mitarbeiter-Hinweis)
                    <br />
                  </>
                )}
                <strong>📅 {t('matching.requested')}:</strong> {new Date(match.erstellt).toLocaleDateString('de-DE')}
              </div>

              {/* Benachrichtigen Button (nur wenn Kontakt/E-Mail vorhanden) */}
              {!match.kontakt ? null : notifiedIds.has(match.id) ? (
                <div style={{
                  padding: '10px',
                  backgroundColor: '#4CAF50',
                  color: '#fff',
                  borderRadius: '6px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  ✅ {t('matching.notificationSent')}
                </div>
              ) : (
                <button
                  onClick={() => handleNotify(match)}
                  disabled={notifying.has(match.id)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: notifying.has(match.id) ? '#ccc' : (colors?.primary || '#2196F3'),
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    cursor: notifying.has(match.id) ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (!notifying.has(match.id)) {
                      e.target.style.backgroundColor = colors?.primaryHover || '#1976D2';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!notifying.has(match.id)) {
                      e.target.style.backgroundColor = colors?.primary || '#2196F3';
                    }
                  }}
                >
                  {notifying.has(match.id) ? `⏳ ${t('matching.sending')}` : `📨 ${t('matching.notify')}`}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Footer mit Schließen-Button */}
        <div style={{ textAlign: 'center', paddingTop: '20px', borderTop: '2px solid #FFB300' }}>
          <button
            onClick={onClose}
            style={{
              padding: '14px 32px',
              backgroundColor: '#757575',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#616161'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#757575'}
          >
            {notifiedIds.size > 0 ? t('matching.done') : t('common.cancel')}
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '15px 30px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'success' ? '#4caf50' : toast.type === 'error' ? '#f44336' : '#2196f3',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10000,
          animation: 'slideUp 0.3s ease-out',
          maxWidth: '90%',
          textAlign: 'center'
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default MatchingModal;
